import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";

import Animation from "@areas/shared/components/chooseLayout/Animation";
import animationStyles from "@areas/shared/components/chooseLayout/styles/animations.module.css";
import { ReactComponent as ArrowIcon } from "@content/img/shared/home/svg/arrow-icon.svg";
import defaultLogo from "@content/img/shared/education/png/default-logo.png";

import { metaActions } from "@store";
import HollandPairTest from "../../components/newStepWalker/HollandPairTest";
import { loadHollandResults } from "../../helpers/hollandStorage";
import { getHollandTypeInfo } from "../../helpers/hollandScores";
import {
  buildCareerSearchText,
  extractSpecialistNeedIds,
  fetchCareerGuidanceByHolland,
  fetchCompaniesBySpecialistNeeds,
  fetchCompaniesWithFacultyByCodes,
} from "../../helpers/hollandApi";

import styles from "./styles/hollandResults.module.css";

function formatSalaryRange(salaryMin, salaryMax) {
  const format = (value) =>
    value != null ? `${Math.round(value).toLocaleString("ru-RU")} ₽` : null;

  const min = format(salaryMin);
  const max = format(salaryMax);

  if (min && max) {
    return `${min} — ${max}`;
  }

  return min || max || "";
}

function Page() {
  const dispatch = useDispatch();
  const [hollandData, setHollandData] = useState(() => loadHollandResults());
  const [careerItems, setCareerItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [load, setLoad] = useState(false);
  const [retake, setRetake] = useState(false);

  useEffect(() => {
    dispatch(
      metaActions.setData({
        icon: "/faviconSchoolboy.ico",
        title: "Результаты теста Холланда",
        description:
          "Сводная информация по результатам профориентационного теста Холланда",
      })
    );
  }, [dispatch]);

  const loadSummary = useCallback(async (data) => {
    if (!data?.hollandApi) {
      setCareerItems([]);
      setCompanies([]);
      setEmployers([]);
      return;
    }

    setLoad(true);

    try {
      const careerRes = await fetchCareerGuidanceByHolland(data.hollandApi, {
        limit: 50,
        offset: 0,
      });
      const items = careerRes.data || [];
      setCareerItems(items);

      const searchText = buildCareerSearchText(items, 8);
      const oksoCodes = [
        ...new Set(items.map((item) => item.OksoCode).filter(Boolean)),
      ].slice(0, 10);

      let facultyRes = { Data: [] };

      if (oksoCodes.length > 0) {
        facultyRes = await fetchCompaniesWithFacultyByCodes({
          text: "",
          faculty: oksoCodes,
          limit: 20,
          offset: 0,
        });
      }

      if (!facultyRes.Data?.length && searchText) {
        facultyRes = await fetchCompaniesWithFacultyByCodes({
          text: searchText,
          limit: 20,
          offset: 0,
        });
      }

      setCompanies(facultyRes.Data || []);

      const specIds = extractSpecialistNeedIds(items);

      if (specIds.length > 0) {
        const employersRes = await fetchCompaniesBySpecialistNeeds({
          specIds,
          limit: 100,
          offset: 0,
          coordinateSize: 3,
        });

        setEmployers(employersRes.Data || []);
      } else {
        setEmployers([]);
      }
    } catch {
      setCareerItems([]);
      setCompanies([]);
      setEmployers([]);
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => {
    if (!retake) {
      loadSummary(hollandData);
    }
  }, [hollandData, loadSummary, retake]);

  const handleTestComplete = (payload) => {
    setHollandData(payload);
    setRetake(false);
  };

  if (retake) {
    return (
      <div className={styles.page}>
        <Animation>
          <div className={styles.container}>
            <HollandPairTest onComplete={handleTestComplete} />
          </div>
        </Animation>
      </div>
    );
  }

  if (!hollandData) {
    return (
      <div className={styles.page}>
        <Animation>
          <div className={`${styles.container} ${styles.emptyState}`}>
            <h1>Результаты теста Холланда</h1>
            <p>Вы ещё не проходили тест. Пройдите его, чтобы увидеть сводку.</p>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => setRetake(true)}
            >
              Пройти тест
            </button>
            <Link to="/fschool" className={styles.backLink}>
              Вернуться на главную
            </Link>
          </div>
        </Animation>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Animation
        animationIn={animationStyles.swipeLeftIn}
        animationOut={animationStyles.swipeRightOut}
      >
        <div className={styles.container}>
          <div className={styles.hero}>
            <div>
              <span className={styles.kicker}>Сводка результатов</span>
              <h1>Тест Холланда</h1>
              <p className={styles.code}>
                Ваш код: <strong>{hollandData.hollandCode}</strong>
              </p>
              {hollandData.completedAt ? (
                <p className={styles.date}>
                  Пройден:{" "}
                  {new Date(hollandData.completedAt).toLocaleString("ru-RU")}
                </p>
              ) : null}
            </div>
            <div className={styles.heroActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setRetake(true)}
              >
                Пройти заново
              </button>
              <Link to="/fschool" className={styles.backLink}>
                На главную
              </Link>
            </div>
          </div>

          {load ? (
            <div className={styles.loader}>
              <ClipLoader />
            </div>
          ) : (
            <>
              <section className={styles.section}>
                <h2>Результаты и описание типов</h2>
                <div className={styles.typeGrid}>
                  {hollandData.sortedTypes?.map((type) => {
                    const info = getHollandTypeInfo(type);
                    const score = hollandData.scores?.[type]?.total || 0;

                    if (!info) return null;

                    return (
                      <article key={type} className={styles.typeCard}>
                        <h3>
                          {type} — {info.title}
                        </h3>
                        <p className={styles.typeScore}>Балл: {score}</p>
                        <p>{info.description}</p>
                      </article>
                    );
                  })}
                </div>
              </section>

              <section className={styles.section}>
                <h2>Специальности и профессии</h2>
                {careerItems.length > 0 ? (
                  <div className={styles.careerList}>
                    {careerItems.map((item) => (
                      <article key={item.Id} className={styles.careerCard}>
                        <div className={styles.match}>
                          Совпадение: {Math.round((item.MatchScore || 0) * 100)}%
                        </div>
                        <div className={styles.okso}>
                          {item.OksoCode ? `${item.OksoCode} — ` : ""}
                          {item.OksoTitle}
                        </div>
                        <div className={styles.okz}>
                          {item.OkzTitle || item.SpecialistNeedsItem}
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className={styles.emptyText}>Подходящие специальности не найдены.</p>
                )}
              </section>

              <section className={styles.section}>
                <h2>Учебные заведения</h2>
                {companies.length > 0 ? (
                  <div className={styles.eduList}>
                    {companies.map((item) => (
                      <article key={item.Id} className={styles.eduCard}>
                        <div className={styles.eduHeader}>
                          <img src={defaultLogo} alt="" />
                          <div>
                            <div className={styles.eduTitle}>
                              {item.Shortname || item.Fullname}
                            </div>
                            <div className={styles.eduAddress}>{item.Address}</div>
                          </div>
                        </div>
                        {item.Faculty?.length ? (
                          <ul className={styles.facultyList}>
                            {item.Faculty.slice(0, 5).map((faculty) => (
                              <li key={faculty.Id}>
                                {faculty.Name || faculty.Title}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        <Link
                          to={`/obrazovanie-v-bashkortostane/${
                            item.IdType === 2 ? "vuzy-rb" : "kolledzhi-rb"
                          }/${item.Slug}`}
                          className={styles.eduLink}
                          target="_blank"
                        >
                          Перейти к учебному заведению
                          <ArrowIcon />
                        </Link>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className={styles.emptyText}>
                    Учебные заведения по результатам теста не найдены.
                  </p>
                )}
              </section>

              <section className={styles.section}>
                <h2>Организации, где требуются специалисты</h2>
                {employers.length > 0 ? (
                  <div className={styles.employerList}>
                    {employers.map((company) => (
                      <article key={company.Id} className={styles.employerCard}>
                        <Link
                          to={`/company/${company.Slug}`}
                          className={styles.employerTitle}
                          target="_blank"
                        >
                          {company.Shortname || company.Fullname || company.Name}
                        </Link>
                        {company.Address ? (
                          <div className={styles.employerAddress}>
                            {company.Address}
                          </div>
                        ) : null}
                        {company.SpecialistNeeds?.map((need) => {
                          const salary = formatSalaryRange(
                            need.SalaryMin,
                            need.SalaryMax
                          );

                          return (
                            <div key={need.Id} className={styles.need}>
                              <div className={styles.needTitle}>
                                {need.Specialist}
                              </div>
                              {need.Industry ? (
                                <div className={styles.needIndustry}>
                                  {need.Industry}
                                </div>
                              ) : null}
                              {salary ? (
                                <div className={styles.needSalary}>{salary}</div>
                              ) : null}
                            </div>
                          );
                        })}
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className={styles.emptyText}>
                    Организации с подходящими потребностями не найдены.
                  </p>
                )}
              </section>
            </>
          )}
        </div>
      </Animation>
    </div>
  );
}

export default Page;
